/**
 * NovelCard Component
 * Displays a novel in a card format with cover, title, status badge, and metadata
 */
import { Link } from "react-router-dom";
import type { Novel, NovelStatus } from "@novelreader/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface NovelCardProps {
  novel: Novel;
  chapterCount?: number;
}

// ---------------------------------------------------------------------------
// Status Badge Configuration
// ---------------------------------------------------------------------------

const statusColors: Record<NovelStatus, string> = {
  draft: "bg-gray-100 text-gray-700",
  ongoing: "bg-blue-100 text-blue-700",
  completed: "bg-green-100 text-green-700",
  hiatus: "bg-yellow-100 text-yellow-700",
};

const statusLabels: Record<NovelStatus, string> = {
  draft: "Draft",
  ongoing: "Ongoing",
  completed: "Completed",
  hiatus: "Hiatus",
};

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

/**
 * Format a date as relative time (Today, Yesterday, X days ago, etc.)
 */
function formatRelativeTime(date: Date | string): string {
  const d = new Date(date);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return d.toLocaleDateString();
}

/**
 * Format chapter count with proper pluralization
 */
function formatChapterCount(count: number | undefined): string {
  if (count === undefined || count === 0) return "No chapters";
  if (count === 1) return "1 chapter";
  return `${count} chapters`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NovelCard({ novel, chapterCount }: NovelCardProps) {
  return (
    <Link
      to={`/novels/${novel.id}/chapters`}
      className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow border border-slate-200 overflow-hidden"
    >
      {/* Cover Image */}
      <div className="aspect-[3/4] bg-slate-100 relative">
        {novel.coverUrl ? (
          <img
            src={novel.coverUrl}
            alt={`${novel.title} cover`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            data-testid="cover-placeholder"
            className="w-full h-full flex items-center justify-center text-slate-400"
          >
            <svg
              className="w-16 h-16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${statusColors[novel.status]}`}
          >
            {statusLabels[novel.status]}
          </span>
        </div>
      </div>

      {/* Card Content */}
      <div className="p-4 space-y-2">
        {/* Title */}
        <h3 className="font-semibold text-slate-900 truncate">{novel.title}</h3>

        {/* Description */}
        <p className="text-sm text-slate-600 line-clamp-2">
          {novel.description || (
            <span className="italic text-slate-400">No description</span>
          )}
        </p>

        {/* Metadata */}
        <div className="flex items-center justify-between text-xs text-slate-500 pt-2">
          <span>{formatChapterCount(chapterCount)}</span>
          <span>{formatRelativeTime(novel.updatedAt)}</span>
        </div>
      </div>
    </Link>
  );
}
